// ═══════════════════════════════════════════════════════════════
// CONTACT REPORTS SERVICE
// Path: src/services/contactReportsService.js
//
// All Supabase operations for the contact_reports table plus the
// "pin to donor record" logic against members.pinned_intelligence
// and the auto-task creation against director_tasks.
//
// Contact reports load ON DEMAND per member — never preloaded into
// AppContext. Mirrors the giftsService pattern.
//
// Blueprint: src/docs/CRM_Donor_Contact_Reports_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { contactLogService } from "./contactLogService";
import { debugLog } from "../utils/debugLog";

// Note: the auto-task creation does NOT happen in this service. It is handled
// by ContactReportsSection.handleSave via AppContext.addTask() so that the
// AppContext directorTasks state stays in sync with the DB. The service only
// covers writes that don't have an existing AppContext-managed local cache.

// ── Field mapping: DB snake_case → JS camelCase ───────────────────
const fromDb = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    memberId: row.member_id,
    contactLogId: row.contact_log_id,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    visitDate: row.visit_date,
    meetingType: row.meeting_type,
    attendees: row.attendees,
    summary: row.summary,
    nextStep: row.next_step,
    nextStepDate: row.next_step_date,
    intelligence: row.intelligence || {},
  };
};

// ── Field mapping: JS camelCase → DB snake_case ───────────────────
const toDb = (data) => {
  const row = {};
  if (data.memberId !== undefined) row.member_id = data.memberId;
  if (data.contactLogId !== undefined) row.contact_log_id = data.contactLogId;
  if (data.createdBy !== undefined) row.created_by = data.createdBy;
  if (data.visitDate !== undefined) row.visit_date = data.visitDate;
  if (data.meetingType !== undefined) row.meeting_type = data.meetingType;
  if (data.attendees !== undefined) row.attendees = data.attendees;
  if (data.summary !== undefined) row.summary = data.summary;
  if (data.nextStep !== undefined) row.next_step = data.nextStep;
  if (data.nextStepDate !== undefined) row.next_step_date = data.nextStepDate;
  if (data.intelligence !== undefined) row.intelligence = data.intelligence;
  return row;
};

export const contactReportsService = {

  // ── Get all contact reports for a single donor ───────────────
  // Newest first. Used by the Member Detail "Contact Reports" tab history list.
  async getByMember(memberId) {
    if (!memberId) return [];
    try {
      const { data, error } = await supabase
        .from("contact_reports")
        .select("*")
        .eq("member_id", memberId)
        .order("visit_date", { ascending: false });

      if (error) throw error;
      return (data || []).map(fromDb);
    } catch (error) {
      debugLog.error("contactReportsService.getByMember", error);
      throw error;
    }
  },

  // ── Get a single contact report by id ────────────────────────
  // Used by the print view route.
  async getById(reportId) {
    if (!reportId) return null;
    try {
      const { data, error } = await supabase
        .from("contact_reports")
        .select("*")
        .eq("id", reportId)
        .single();

      if (error) throw error;
      return fromDb(data);
    } catch (error) {
      debugLog.error("contactReportsService.getById", error);
      throw error;
    }
  },

  // ── Get filtered contact reports across all donors ───────────
  // Used by ReportsView. Filters: { sinceDate, untilDate, createdBy, meetingType, memberId }
  // Joins members for the donor name column.
  async getAll(filters = {}) {
    try {
      let query = supabase
        .from("contact_reports")
        .select("*, members (id, first_name, last_name, credentialed_full_name)")
        .order("visit_date", { ascending: false });

      if (filters.sinceDate) query = query.gte("visit_date", filters.sinceDate);
      if (filters.untilDate) query = query.lte("visit_date", filters.untilDate);
      if (filters.createdBy) query = query.eq("created_by", filters.createdBy);
      if (filters.meetingType) query = query.eq("meeting_type", filters.meetingType);
      if (filters.memberId) query = query.eq("member_id", filters.memberId);

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map((row) => ({
        ...fromDb(row),
        memberName: row.members
          ? row.members.credentialed_full_name ||
            `${row.members.first_name} ${row.members.last_name}`.trim()
          : null,
      }));
    } catch (error) {
      debugLog.error("contactReportsService.getAll", error);
      throw error;
    }
  },

  // ── Compliance gap: members with no recent contact report ────
  // Used by the ReportsView "Active prospects with no contact in N days" cut.
  // Returns members joined with their most recent contact_reports.visit_date.
  async getComplianceGap(daysSince = 90) {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysSince);
      const cutoffStr = cutoffDate.toISOString().slice(0, 10);

      // Get all reports newer than the cutoff — these are the "covered" members
      const { data: recent, error: recentError } = await supabase
        .from("contact_reports")
        .select("member_id")
        .gte("visit_date", cutoffStr);

      if (recentError) throw recentError;

      const coveredMemberIds = new Set((recent || []).map((r) => r.member_id));

      // Get active members in cultivation-track pathways with no recent report
      const { data: members, error: membersError } = await supabase
        .from("members")
        .select("id, first_name, last_name, credentialed_full_name, pathway, assigned_director, status")
        .eq("status", "active")
        .in("pathway", ["connect", "contribute", "lead"])
        .order("last_name", { ascending: true });

      if (membersError) throw membersError;

      const gap = (members || []).filter((m) => !coveredMemberIds.has(m.id));

      // For each gap member, look up their most recent report (could be older than cutoff)
      const memberIds = gap.map((m) => m.id);
      let lastReports = {};
      if (memberIds.length > 0) {
        const { data: oldReports, error: oldError } = await supabase
          .from("contact_reports")
          .select("member_id, visit_date")
          .in("member_id", memberIds)
          .order("visit_date", { ascending: false });

        if (oldError) throw oldError;

        for (const r of oldReports || []) {
          if (!lastReports[r.member_id]) lastReports[r.member_id] = r.visit_date;
        }
      }

      const today = new Date();
      return gap.map((m) => {
        const lastDate = lastReports[m.id] || null;
        const daysSinceLast = lastDate
          ? Math.floor((today - new Date(lastDate)) / (1000 * 60 * 60 * 24))
          : null;
        return {
          memberId: m.id,
          memberName:
            m.credentialed_full_name ||
            `${m.first_name} ${m.last_name}`.trim(),
          pathway: m.pathway,
          assignedDirector: m.assigned_director || null,
          lastContactReportDate: lastDate,
          daysSinceLastContactReport: daysSinceLast,
        };
      });
    } catch (error) {
      debugLog.error("contactReportsService.getComplianceGap", error);
      throw error;
    }
  },

  // ── Create a contact report (atomic-ish flow) ────────────────
  // Writes 3 things in sequence:
  //   1. The contact_reports row
  //   2. A linked contact_logs row (type="meeting") for engagement scoring
  //   3. Pinned sections (if any) to members.pinned_intelligence
  //
  // The auto-task is created by the caller (ContactReportsSection) using
  // AppContext.addTask() so the directorTasks local state updates immediately.
  //
  // If step 2 fails, step 1 is rolled back manually.
  // If step 3 fails, the report still exists; caller is told via the returned
  // `warnings` array and can retry from the UI.
  //
  // @param memberId  uuid
  // @param report    { visitDate, meetingType, attendees, summary, nextStep, nextStepDate, intelligence }
  // @param pins      { family: bool, occupation: bool, recreation: bool, messagingEmphasize: bool, messagingAvoid: bool }
  // @param options   { createdBy, pinnedByName }
  // @returns         { report, warnings }
  async create(memberId, report, pins = {}, options = {}) {
    if (!memberId) throw new Error("memberId is required");
    if (!options.createdBy) throw new Error("options.createdBy is required");

    const warnings = [];
    let createdReport = null;

    // ── Step 1: insert the contact_reports row ──
    try {
      const row = {
        ...toDb(report),
        member_id: memberId,
        created_by: options.createdBy,
      };
      const { data, error } = await supabase
        .from("contact_reports")
        .insert(row)
        .select()
        .single();
      if (error) throw error;
      createdReport = fromDb(data);
      debugLog.action("contactReportsService.create — report inserted", { id: createdReport.id });
    } catch (error) {
      debugLog.error("contactReportsService.create — step 1 (report insert)", error);
      throw error;
    }

    // ── Step 2: insert linked contact_logs row ──
    let linkedLogId = null;
    try {
      const log = await contactLogService.create(memberId, {
        type: "meeting",
        subject: "Donor visit — contact report filed",
        notes: report.summary || "",
        date: report.visitDate || new Date().toISOString(),
      });
      linkedLogId = log.id;
    } catch (error) {
      // Rollback the report since the log is required for engagement scoring
      debugLog.error("contactReportsService.create — step 2 (log insert)", error);
      try {
        await supabase.from("contact_reports").delete().eq("id", createdReport.id);
      } catch (rollbackError) {
        debugLog.error("contactReportsService.create — rollback failed", rollbackError);
      }
      throw error;
    }

    // ── Step 2b: write the contact_log_id back onto the report ──
    try {
      const { data, error } = await supabase
        .from("contact_reports")
        .update({ contact_log_id: linkedLogId })
        .eq("id", createdReport.id)
        .select()
        .single();
      if (error) throw error;
      createdReport = fromDb(data);
    } catch (error) {
      debugLog.error("contactReportsService.create — step 2b (link back)", error);
      warnings.push("Contact log was created but could not be linked back to the report. Engagement scoring still works.");
    }

    // ── Step 3: write pinned sections to members.pinned_intelligence ──
    const pinKeyMap = {
      family: "family",
      occupation: "occupation",
      recreation: "recreation",
      messagingEmphasize: "messaging_emphasize",
      messagingAvoid: "messaging_avoid",
    };
    const pinsToWrite = Object.entries(pins).filter(([, on]) => on);
    if (pinsToWrite.length > 0) {
      try {
        // Read current pinned_intelligence
        const { data: member, error: readError } = await supabase
          .from("members")
          .select("pinned_intelligence")
          .eq("id", memberId)
          .single();
        if (readError) throw readError;

        const current = member.pinned_intelligence || {};
        const updated = { ...current };
        const intel = report.intelligence || {};
        const nowIso = new Date().toISOString();

        for (const [pinFlag] of pinsToWrite) {
          const dbKey = pinKeyMap[pinFlag];
          if (!dbKey) continue;

          // Pull text out of the report's intelligence object
          let text = "";
          if (pinFlag === "messagingEmphasize") {
            text = (intel.messaging && intel.messaging.emphasize) || "";
          } else if (pinFlag === "messagingAvoid") {
            text = (intel.messaging && intel.messaging.avoid) || "";
          } else {
            const section = intel[pinFlag];
            text = (section && section.notes) || "";
          }

          if (!text || !text.trim()) continue; // nothing to pin

          updated[dbKey] = {
            text,
            source_report_id: createdReport.id,
            pinned_at: nowIso,
            pinned_by: options.createdBy,
            pinned_by_name: options.pinnedByName || null,
          };
        }

        const { error: writeError } = await supabase
          .from("members")
          .update({ pinned_intelligence: updated })
          .eq("id", memberId);
        if (writeError) throw writeError;
      } catch (error) {
        debugLog.error("contactReportsService.create — step 3 (pin sections)", error);
        warnings.push("Some pinned sections could not be saved to the donor record. The contact report is fine.");
      }
    }

    // Note: auto-task creation is the caller's responsibility — see file header.
    return { report: createdReport, warnings };
  },

  // ── Update a contact report ──────────────────────────────────
  // Does NOT touch the linked contact_log entry, the pinned_intelligence,
  // or the auto-created task. Pin updates go through pinSection / unpinSection.
  async update(reportId, updates) {
    if (!reportId) throw new Error("reportId is required");
    try {
      const { data, error } = await supabase
        .from("contact_reports")
        .update(toDb(updates))
        .eq("id", reportId)
        .select()
        .single();
      if (error) throw error;
      debugLog.action("contactReportsService.update", { reportId });
      return fromDb(data);
    } catch (error) {
      debugLog.error("contactReportsService.update", error);
      throw error;
    }
  },

  // ── Delete a contact report ──────────────────────────────────
  // The linked contact_log row stays (engagement scoring history is preserved).
  // Pinned items sourced from this report survive as last-known-good.
  // The auto-created task's source_report_id is set to NULL by the FK.
  async remove(reportId) {
    if (!reportId) throw new Error("reportId is required");
    try {
      const { error } = await supabase
        .from("contact_reports")
        .delete()
        .eq("id", reportId);
      if (error) throw error;
      debugLog.action("contactReportsService.remove", { reportId });
    } catch (error) {
      debugLog.error("contactReportsService.remove", error);
      throw error;
    }
  },

  // ── Pin a section to a member's pinned_intelligence ──────────
  // Replaces any existing pin for that section.
  async pinSection(memberId, sectionKey, text, sourceReportId, pinnedBy, pinnedByName) {
    if (!memberId || !sectionKey) {
      throw new Error("memberId and sectionKey are required");
    }
    try {
      const { data: member, error: readError } = await supabase
        .from("members")
        .select("pinned_intelligence")
        .eq("id", memberId)
        .single();
      if (readError) throw readError;

      const current = member.pinned_intelligence || {};
      const updated = {
        ...current,
        [sectionKey]: {
          text,
          source_report_id: sourceReportId,
          pinned_at: new Date().toISOString(),
          pinned_by: pinnedBy,
          pinned_by_name: pinnedByName || null,
        },
      };

      const { error: writeError } = await supabase
        .from("members")
        .update({ pinned_intelligence: updated })
        .eq("id", memberId);
      if (writeError) throw writeError;

      debugLog.action("contactReportsService.pinSection", { memberId, sectionKey });
    } catch (error) {
      debugLog.error("contactReportsService.pinSection", error);
      throw error;
    }
  },

  // ── Unpin a section ──────────────────────────────────────────
  async unpinSection(memberId, sectionKey) {
    if (!memberId || !sectionKey) {
      throw new Error("memberId and sectionKey are required");
    }
    try {
      const { data: member, error: readError } = await supabase
        .from("members")
        .select("pinned_intelligence")
        .eq("id", memberId)
        .single();
      if (readError) throw readError;

      const current = member.pinned_intelligence || {};
      const updated = { ...current };
      delete updated[sectionKey];

      const { error: writeError } = await supabase
        .from("members")
        .update({ pinned_intelligence: updated })
        .eq("id", memberId);
      if (writeError) throw writeError;

      debugLog.action("contactReportsService.unpinSection", { memberId, sectionKey });
    } catch (error) {
      debugLog.error("contactReportsService.unpinSection", error);
      throw error;
    }
  },
};
